import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useConsoleWS } from '../hooks/useConsoleWS';
import api from '../utils/api';
import {
  ChevronLeft, Play, Square, RotateCcw, Terminal,
  Wifi, WifiOff, Send, Trash2, Download, Loader
} from 'lucide-react';
import toast from 'react-hot-toast';

// Strip ANSI escape codes that Pterodactyl includes in console output
// eslint-disable-next-line no-control-regex
const ANSI_REGEX = /[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g;
const stripAnsi = (line) => (line || '').replace(ANSI_REGEX, '');

// Colorize console output based on content
const colorize = (line) => {
  if (!line) return '';
  if (line.includes('/ERROR') || line.includes('[Error]')) return 'text-red-400';
  if (line.includes('/WARN')) return 'text-yellow-400';
  if (line.includes('Done') && line.includes('For help')) return 'text-green-400';
  if (line.includes('[Status]')) return 'text-yellow-300';
  if (line.startsWith('>')) return 'text-cyan-400';
  if (line.includes('joined the game')) return 'text-green-300';
  if (line.includes('left the game')) return 'text-orange-300';
  return 'text-gray-300';
};

const QUICK_COMMANDS = [
  { label: 'List players', cmd: 'list' },
  { label: 'Save world', cmd: 'save-all' },
  { label: 'Day', cmd: 'time set day' },
  { label: 'Clear weather', cmd: 'weather clear' },
  { label: 'Whitelist', cmd: 'whitelist list' },
];

const ConsolePage = () => {
  const { id } = useParams();
  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [command, setCommand] = useState('');
  const [cmdHistory, setCmdHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [powering, setPowering] = useState(null); // 'start' | 'stop' | 'restart' | null
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const { lines, connected, status, serverState, connect, disconnect, sendCommand, clearLines } = useConsoleWS(id);

  useEffect(() => {
    api.get(`/servers/${id}`).then(({ data }) => {
      setServer(data.server);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  // Auto-connect to console when page opens
  useEffect(() => {
    if (!loading && server) {
      const t = setTimeout(connect, 300);
      return () => clearTimeout(t);
    }
  }, [loading, server, connect]);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  // Power actions — wired to the same API the dashboard uses
  const handlePower = async (action) => {
    setPowering(action);
    try {
      await api.post(`/servers/${id}/power`, { action });
      toast.success(`Server ${action} sent`);
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${action} server`);
    } finally {
      setPowering(null);
    }
  };

  const handleSend = (e) => {
    e?.preventDefault();
    if (!command.trim()) return;
    sendCommand(command.trim());
    setCmdHistory((prev) => [command.trim(), ...prev.slice(0, 49)]);
    setCommand('');
    setHistoryIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = Math.min(historyIdx + 1, cmdHistory.length - 1);
      setHistoryIdx(next);
      setCommand(cmdHistory[next] || '');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.max(historyIdx - 1, -1);
      setHistoryIdx(next);
      setCommand(next === -1 ? '' : cmdHistory[next]);
    }
  };

  const downloadLogs = () => {
    const blob = new Blob([lines.map(stripAnsi).join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${server?.name || 'server'}-console.log`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Server power state from the live WebSocket (most accurate source)
  const isRunning = serverState === 'running';
  const isTransitioning = serverState === 'starting' || serverState === 'stopping';

  const STATUS_CONFIG = {
    connected: { color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/20', dot: 'bg-green-400 animate-pulse', label: 'Connected' },
    connecting: { color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20', dot: 'bg-yellow-400 animate-pulse', label: 'Connecting...' },
    disconnected: { color: 'text-gray-400', bg: 'bg-gray-500/10 border-gray-500/20', dot: 'bg-gray-500', label: 'Disconnected' },
    error: { color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', dot: 'bg-red-400', label: 'Error' },
  };
  const statusCfg = STATUS_CONFIG[status];

  return (
    <div className="min-h-screen bg-gray-950 pt-16 flex flex-col">
      {/* Top bar */}
      <div className="border-b border-gray-800 bg-gray-900/80 backdrop-blur px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-gray-400 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Terminal className="w-5 h-5 text-green-400" />
              <span className="font-bold text-white">{server?.name || 'Server Console'}</span>
              {server?.plan_name && <span className="text-gray-500 text-sm">· {server.plan_name}</span>}
              {serverState && (
                <span className={`text-xs px-2 py-0.5 rounded-full border ${
                  isRunning ? 'bg-green-500/10 border-green-500/20 text-green-400'
                  : isTransitioning ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'
                  : 'bg-gray-500/10 border-gray-500/20 text-gray-400'
                }`}>
                  {serverState}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Console connection badge */}
            <div className={`flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs font-medium ${statusCfg.bg} ${statusCfg.color}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
              {statusCfg.label}
            </div>

            {/* Power buttons — now actually wired up */}
            <div className="flex gap-1">
              <button
                onClick={() => handlePower('start')}
                disabled={powering !== null || isRunning || isTransitioning}
                className="p-2 bg-gray-800 hover:bg-green-500/10 hover:text-green-400 border border-gray-700 rounded-lg text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Start"
              >
                {powering === 'start' ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              </button>
              <button
                onClick={() => handlePower('stop')}
                disabled={powering !== null || (!isRunning && serverState !== null)}
                className="p-2 bg-gray-800 hover:bg-red-500/10 hover:text-red-400 border border-gray-700 rounded-lg text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Stop"
              >
                {powering === 'stop' ? <Loader className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />}
              </button>
              <button
                onClick={() => handlePower('restart')}
                disabled={powering !== null || (!isRunning && serverState !== null)}
                className="p-2 bg-gray-800 hover:bg-yellow-500/10 hover:text-yellow-400 border border-gray-700 rounded-lg text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                title="Restart"
              >
                {powering === 'restart' ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              </button>
            </div>

            {/* Connect/Disconnect */}
            {connected ? (
              <button onClick={disconnect} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-lg text-sm transition-colors">
                <WifiOff className="w-4 h-4" /> Disconnect
              </button>
            ) : (
              <button onClick={connect} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 rounded-lg text-sm transition-colors">
                <Wifi className="w-4 h-4" /> Connect
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 max-w-7xl mx-auto w-full gap-4 p-4">
        {/* Console output */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-500 text-xs font-mono">{lines.length} lines</span>
            <div className="flex gap-2">
              <button onClick={downloadLogs} className="flex items-center gap-1 text-gray-500 hover:text-gray-300 text-xs transition-colors">
                <Download className="w-3 h-3" /> Export
              </button>
              <button
                onClick={() => { clearLines(); toast.success('Console cleared'); }}
                className="flex items-center gap-1 text-gray-500 hover:text-red-400 text-xs transition-colors"
              >
                <Trash2 className="w-3 h-3" /> Clear
              </button>
            </div>
          </div>

          {/* Terminal */}
          <div
            className="flex-1 bg-gray-950 border border-gray-800 rounded-xl p-4 font-mono text-xs overflow-y-auto"
            style={{ minHeight: '400px', maxHeight: 'calc(100vh - 280px)' }}
            onClick={() => inputRef.current?.focus()}
          >
            {lines.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-600">
                <Terminal className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">Console is empty</p>
                <p className="text-xs mt-1">{connected ? 'Waiting for server output...' : 'Click "Connect" to start streaming logs'}</p>
              </div>
            ) : (
              lines.map((line, i) => {
                const clean = stripAnsi(line);
                return (
                  <div key={i} className={`leading-5 whitespace-pre-wrap break-all ${colorize(clean)}`}>
                    {clean}
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Command input */}
          <form onSubmit={handleSend} className="flex gap-2 mt-2">
            <div className="flex-1 flex items-center bg-gray-900 border border-gray-700 focus-within:border-green-500 rounded-xl px-4 transition-colors">
              <span className="text-green-400 font-mono text-sm mr-2">$</span>
              <input
                ref={inputRef}
                type="text"
                value={command}
                onChange={(e) => setCommand(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={!connected}
                placeholder={connected ? 'Type a command... (↑↓ for history)' : 'Connect to send commands'}
                className="flex-1 bg-transparent py-3 text-white font-mono text-sm focus:outline-none placeholder-gray-600 disabled:opacity-40"
              />
            </div>
            <button
              type="submit"
              disabled={!connected || !command.trim()}
              className="px-4 py-3 bg-green-500 hover:bg-green-400 disabled:opacity-30 disabled:cursor-not-allowed text-gray-950 rounded-xl transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>

        {/* Sidebar - quick commands */}
        <div className="w-52 flex-shrink-0 space-y-4">
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Quick Commands</h3>
            <div className="space-y-1.5">
              {QUICK_COMMANDS.map(({ label, cmd }) => (
                <button
                  key={cmd}
                  onClick={() => { sendCommand(cmd); toast.success(`Sent: ${cmd}`); }}
                  disabled={!connected}
                  className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {server && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Server Info</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">RAM</span>
                  <span className="text-white">{server.ram_mb / 1024}GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Storage</span>
                  <span className="text-white">{server.disk_mb / 1024}GB</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Slots</span>
                  <span className="text-white">{server.player_slots}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Type</span>
                  <span className="text-white capitalize">{server.server_type || 'Paper'}</span>
                </div>
                {server.ip_address && (
                  <div className="pt-2 border-t border-gray-800">
                    <span className="text-gray-500 block mb-1">Address</span>
                    <button
                      onClick={() => { navigator.clipboard.writeText(`${server.ip_address}:${server.port}`); toast.success('Address copied'); }}
                      className="text-green-400 hover:text-green-300 font-mono text-xs break-all text-left transition-colors"
                    >
                      {server.ip_address}:{server.port}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsolePage;
